/* eslint-disable max-lines */
import type { StorageResponsePayload } from "@swiggy-private/native-bridge";
import { webStorage } from "./storage";

// Constants
export const POPUP_WIDGET_FREQUENCY_TIMEOFFSET_MS = 3 * 60 * 60 * 1000; // 3 hours
export const POPUP_WIDGET_SHOWN_STORAGE_KEY = "popup_widget_shown";

// Types
export interface IPopupFrequencyData {
    [tableId: string]: {
        entities: {
            [entityId: string]: {
                timestamp: string; // Timestamp when first shown (used for duration check)
                count: number; // Number of times shown within duration
                maxCount: number; // Max count allowed (from API)
            };
        };
    };
}

interface CheckPopupFrequencyCappingParams {
    key: string;
    tableId: string;
    entityId: string;
    frequencyCappingConfig: {
        isFrequencyCappingEnabled: boolean;
        maxCount?: number; // Default: 1
    };
    duration?: number; // Defaults to POPUP_WIDGET_FREQUENCY_TIMEOFFSET_MS
}

/**
 * Check if a popup should be shown based on frequency capping rules
 * @returns true if popup should be capped (don't show), false if should show
 */
export const checkPopupFrequencyCapping = async ({
    key,
    tableId,
    entityId,
    frequencyCappingConfig,
    duration = POPUP_WIDGET_FREQUENCY_TIMEOFFSET_MS,
}: CheckPopupFrequencyCappingParams): Promise<boolean> => {
    try {
        // If frequency capping is disabled, always show popup
        if (!frequencyCappingConfig.isFrequencyCappingEnabled) {
            return false;
        }

        // Get stored data
        const response = await webStorage({ type: "GET", data: { [key]: "" } });
        const dataStr = (response as StorageResponsePayload)?.data?.[key];
        const data: IPopupFrequencyData = JSON.parse(dataStr ?? "{}");

        const tableData = data[tableId];
        if (!tableData || !tableData.entities || !tableData.entities[entityId]) {
            return false;
        }

        const entityData = tableData.entities[entityId];
        const maxCount = frequencyCappingConfig.maxCount ?? 1;
        const timestamp = Number(entityData.timestamp);
        const count = Number(entityData.count ?? 0);

        const timeDiff = Date.now() - timestamp;
        if (timeDiff >= duration) {
            delete tableData.entities[entityId];
            if (Object.keys(tableData.entities).length === 0) {
                delete data[tableId];
            } else {
                data[tableId] = tableData;
            }
            await webStorage({ type: "SAVE", data: { [key]: JSON.stringify(data) } });
            return false;
        }

        const effectiveMaxCount = Number(entityData.maxCount ?? maxCount);

        if (count >= effectiveMaxCount) {
            return true;
        }
        return false;
    } catch (error) {
        return false;
    }
};

interface UpdatePopupFrequencyCappingParams {
    key: string;
    tableId: string;
    entityId: string;
    maxCount?: number; // Default: 1
}

/**
 * Update frequency capping storage when popup is shown
 */
export const updatePopupFrequencyCappingWithConfig = async ({
    key,
    tableId,
    entityId,
    maxCount = 1,
}: UpdatePopupFrequencyCappingParams): Promise<StorageResponsePayload | undefined> => {
    try {
        const response = await webStorage({ type: "GET", data: { [key]: "" } });
        const dataStr = (response as StorageResponsePayload)?.data?.[key];
        const data: IPopupFrequencyData = JSON.parse(dataStr ?? "{}");

        if (!data[tableId]) {
            data[tableId] = { entities: {} };
        }

        const tableData = data[tableId];
        if (!tableData.entities) {
            tableData.entities = {};
        }

        if (tableData.entities[entityId]) {
            tableData.entities[entityId].count = (tableData.entities[entityId].count ?? 0) + 1;
        } else {
            tableData.entities[entityId] = {
                timestamp: String(Date.now()),
                count: 1,
                maxCount,
            };
        }
        const payload = JSON.stringify(data);
        const saveResponse = await webStorage({ type: "SAVE", data: { [key]: payload } });
        if (saveResponse && (saveResponse as StorageResponsePayload).data) {
            return saveResponse as StorageResponsePayload;
        }
        return;
    } catch (error) {
        return undefined;
    }
};
